import { useEffect, useRef, useState } from "react";
import { useGetUserDetailsMutation } from "redux/api/authApi";

type AudioMap = Record<string, string>;

const PREFETCH_COUNT = 3;
const TARGET_WORDS_PER_CHUNK = 30;

export const useTTSStream = () => {
  const [isMuted, setIsMuted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const audioMapRef = useRef<AudioMap>({});
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioInstancesRef = useRef<HTMLAudioElement[]>([]);

  const generationRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [userDetails, setUserDetails] = useState<any>();
  const [getUserDetailsMutation] = useGetUserDetailsMutation();
  const useremail = localStorage.getItem('user_email') || '';

  //const DEFAULT_VOICE = userDetails?.digitial_twin_voice ? userDetails?.digitial_twin_voice : "default.wav";
  const DEFAULT_VOICE = userDetails?.digitial_twin_voice
                    ? userDetails.digitial_twin_voice
                    : userDetails?.default_voice?.toLowerCase() === "female"
                    ? "default_female.wav"
                    : "default.wav";


  // ─────────────────────────────────────────────
  // MUTE SYNC
  // ─────────────────────────────────────────────
  useEffect(() => {
    audioInstancesRef.current.forEach((a) => {
      try {
        a.muted = isMuted;
      } catch { }
    });

    if (audioRef.current) {
      try {
        audioRef.current.muted = isMuted;
      } catch { }
    }
  }, [isMuted]);

  // ─────────────────────────────────────────────
  // CLEANUP ON UNMOUNT
  // ─────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopAll();
    };
  }, []);

  // ─────────────────────────────────────────────
  // SPLIT TEXT
  // ─────────────────────────────────────────────
  const splitText = (text: string): string[] => {
    if (!text) return [];

    text = text
      .replace(/\*+/g, " ")
      .replace(/#+/g, " ")
      .replace(/_+/g, " ")
      .replace(/`+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    const sentences =
      text.match(/[^.!?]+[.!?]+/g) || [text];

    const chunks: string[] = [];

    let current = "";

    for (const sent of sentences) {
      const trimmed = sent.trim();

      if (!trimmed) continue;

      const combinedWords = (
        current +
        " " +
        trimmed
      ).split(/\s+/).length;

      if (
        current &&
        combinedWords > TARGET_WORDS_PER_CHUNK
      ) {
        chunks.push(current.trim());

        current = trimmed;
      } else {
        current = current
          ? current + " " + trimmed
          : trimmed;
      }
    }

    if (current.trim()) {
      chunks.push(current.trim());
    }

    return chunks;
  };

  // ─────────────────────────────────────────────
  // STOP ALL
  // ─────────────────────────────────────────────
  const stopAll = () => {
    generationRef.current++;

    abortControllerRef.current?.abort();

    // cleanup blob urls
    Object.values(audioMapRef.current).forEach(
      (url) => {
        try {
          if (url?.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        } catch { }
      }
    );

    audioMapRef.current = {};

    audioInstancesRef.current.forEach((a) => {
      try {
        a.pause();
        a.currentTime = 0;
        a.onended = null;
        a.onerror = null;
        // a.onloadedmetadata = null;
        a.oncanplaythrough = null;
        a.src = "";
      } catch { }
    });

    audioInstancesRef.current = [];

    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch { }

      audioRef.current = null;
    }

    setIsPlaying(false);
    setCurrentIndex(0);
  };

  const stop = () => stopAll();

  const fetchData = async () => {
    try {

      const response = await getUserDetailsMutation({ email: useremail, filter: '', start_date: '', end_date: '' });

      if ('data' in response) {
        const details = response.data.data;
        setUserDetails(details);
        console.log('userdetails from view course audio streaming =>', details);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ─────────────────────────────────────────────
  // PLAY TEXT
  // ─────────────────────────────────────────────
  const playText = async (
    text: string,
    voice: string = DEFAULT_VOICE,
    onChunkPlayed?: (
    chunkIndex: number,
    totalChunks: number
  ) => void
  ) => {
    stopAll();

    const myGeneration =
      generationRef.current;

    abortControllerRef.current =
      new AbortController();

    const signal =
      abortControllerRef.current.signal;

    const chunks = splitText(text);

    if (!chunks.length) return;

    setIsPlaying(true);

    const audioUrls: (
      | string
      | null
      | undefined
    )[] = new Array(chunks.length);

    let fetchIndex = 0;

    // ─────────────────────────────────────────
    // FETCH SINGLE CHUNK
    // ─────────────────────────────────────────
    const fetchChunk = async (
      index: number
    ) => {
      if (index >= chunks.length) return;

      try {
        const query = new URLSearchParams({
          text: chunks[index],
          voice,
        }).toString();
        const res = await fetch(
          `${process.env.REACT_APP_TEXT_TO_SPEECH_SERVICE}/speak_stream?${query}`,
          {
            method: "POST",
            signal,
          }
        );

        if (!res.ok) {
          throw new Error(
            `HTTP ${res.status}`
          );
        }

        const blob = await res.blob();

        if (!blob || blob.size === 0) {
          audioUrls[index] = null;
          return;
        }

        const blobUrl =
          URL.createObjectURL(blob);

        audioUrls[index] = blobUrl;

        audioMapRef.current[
          `chunk-${index}`
        ] = blobUrl;
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          console.error(
            "Chunk fetch failed",
            err
          );
        }

        audioUrls[index] = null;
      }
    };

    // ─────────────────────────────────────────
    // INITIAL PREFETCH
    // ─────────────────────────────────────────
    const starters = [];

    for (
      let i = 0;
      i <
      Math.min(
        PREFETCH_COUNT,
        chunks.length
      );
      i++
    ) {
      starters.push(fetchChunk(fetchIndex++));
    }

    await Promise.all(starters);

    // ─────────────────────────────────────────
    // PLAY SEQUENTIALLY
    // ─────────────────────────────────────────
    for (let i = 0; i < chunks.length; i++) {
      if (
        myGeneration !==
        generationRef.current
      ) {
        break;
      }

      setCurrentIndex(i);

      // prefetch next chunk
      if (fetchIndex < chunks.length) {
        fetchChunk(fetchIndex++);
      }

      // wait until chunk available
      while (audioUrls[i] === undefined) {
        if (
          myGeneration !==
          generationRef.current
        ) {
          setIsPlaying(false);
          return;
        }

        await new Promise((r) =>
          setTimeout(r, 50)
        );
      }

      if (audioUrls[i] === null) {
        continue;
      }

      /* await new Promise<void>((resolve) => {
        const audio = new Audio(
          audioUrls[i]!
        );

        audioRef.current = audio;

        audioInstancesRef.current.push(
          audio
        );

        audio.muted = isMuted;
        audio.preload = "auto";

        let settled = false;

        const finish = () => {
          if (settled) return;

          settled = true;

          audio.onended = null;
          audio.onerror = null;
          // audio.onloadedmetadata = null;
          audio.oncanplaythrough = null;

          try {
            audio.pause();
          } catch { }

          if (audio.src?.startsWith("blob:")) {
            URL.revokeObjectURL(audio.src);
          }

          resolve();
        };

        audio.onended = () => {
          finish();
        };

        audio.onerror = (e) => {
          console.error(
            "Audio playback failed",
            e
          );

          finish();
        };

        audio.oncanplaythrough = async () => {
          if (
            myGeneration !==
            generationRef.current
          ) {
            finish();
            return;
          }

          try {
            await audio.play();
          } catch (err) {
            console.error(
              "Audio play failed",
              err
            );

            finish();
          }
        };
      }); */

      const chunkPlayedSuccessfully = await new Promise<boolean>(
  (resolve) => {
    const audio = new Audio(
      audioUrls[i]!
    );

    audioRef.current = audio;

    audioInstancesRef.current.push(audio);

    audio.muted = isMuted;
    audio.preload = "auto";

    let settled = false;

    const finish = (success: boolean) => {
      if (settled) return;

      settled = true;

      audio.onended = null;
      audio.onerror = null;
      audio.oncanplaythrough = null;

      try {
        audio.pause();
      } catch {}

      if (audio.src?.startsWith("blob:")) {
        URL.revokeObjectURL(audio.src);
      }

      resolve(success);
    };

    audio.onended = () => {
      finish(true);
    };

    audio.onerror = (e) => {
      console.error(
        "Audio playback failed",
        e
      );

      finish(false);
    };

    audio.oncanplaythrough = async () => {
      if (
        myGeneration !==
        generationRef.current
      ) {
        finish(false);
        return;
      }

      try {
        await audio.play();
      } catch (err) {
        console.error(
          "Audio play failed",
          err
        );

        finish(false);
      }
    };
  }
);

if (
  chunkPlayedSuccessfully &&
  onChunkPlayed
) {
  onChunkPlayed(
    i,
    chunks.length
  );
}
    }

    if (
      myGeneration ===
      generationRef.current
    ) {
      setIsPlaying(false);
    }
  };

  // ─────────────────────────────────────────────
  // PAUSE
  // ─────────────────────────────────────────────
  const pause = () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();

        setIsPlaying(false);
      } catch { }
    }
  };

  // ─────────────────────────────────────────────
  // RESUME
  // ─────────────────────────────────────────────
  const resume = async () => {
    if (audioRef.current) {
      try {
        await audioRef.current.play();

        setIsPlaying(true);
      } catch (err) {
        console.error(
          "Resume failed",
          err
        );
      }
    }
  };

  // ─────────────────────────────────────────────
  // SEEK
  // ─────────────────────────────────────────────
  const seek = async (
    text: string,
    _index: number
  ) => {
    stopAll();

    await playText(text);
  };

  return {
    playText,
    pause,
    resume,
    stop,
    stopAll,
    seek,
    isPlaying,
    currentIndex,
    audioRef,
    isMuted,
    setIsMuted,
  };
};

export default useTTSStream;